import { useEffect, useReducer, useRef, useState } from 'react';
import { CaretDown } from '@phosphor-icons/react/dist/ssr/CaretDown';
import { Scan } from '@phosphor-icons/react/dist/ssr/Scan';
import CustomQRDisplay from '../../../components/QR/CustomQRDisplay';
import SheetOverlay, {
  SheetDismissX,
  SheetSpinner,
  SheetStrip,
} from '../../../components/UISystem/SheetOverlay';
import { useConnectionContext, useConnectionData } from '../../../contexts/ConnectionStateContext';
import {
  codePageGate,
  codeTaken,
  initialGate,
  STAGE,
  WAITING_EXPIRE_MS,
} from '../../../services/exchange/codePageGate';
import { loadShareSheetData } from '../../../services/sharing/shareSheetData';
import ExchangeShareSheet, { SHARE_RISE_MS } from '../ExchangeShare/ExchangeShareSheet';
import useCodePageOffer from './useCodePageOffer';
import s from './GuestCodePage.module.css';

/**
 * The host's own code page, new design — Simple and Full alike (signup E3a
 * Step 9 for a Simple; E3b-1 step 9 gives it to every host. FINALS 5A.8 /
 * 5B.1, code-screen.jsx `RoyaleCode bar={false}` + `RoyaleCaret`). QRScreen
 * renders it under isNewExperience() — a render swap only: the session, the
 * detection layers and formation all stay QRScreen's.
 *
 * Until someone scans, it is the whole screen: ROYALE, the code, the caret
 * back to People. The rest is codePageGate's (SIGNUP_Profile §0, §3):
 * - **The scan raises the share sheet** 420 ms later (ExchangeShareSheet —
 *   the ruled matrix, shareSheetRules).
 * - **A share, then the waiting gate** until the guest's name arrives (a
 *   Profile scanner's name is already known: straight on). The waiting sheet
 *   is the quiet one, with the bare X; the X or 60 s drop back to the bare
 *   code, the room kept — a late name moves nothing.
 * - **A tap outside the share sheet** folds it to People, nothing more shared.
 * - **A repeat connect** raises the repeat sheet instead (rule 7, plan step
 *   13): `Reconnect` shares nothing new and lands on People (the name is
 *   known, so no waiting); `Share more`, when shown, swaps in the first-connect
 *   sheet.
 * Every way out lands on new People with the person just met on top (`onDone`).
 * - **The caret shows only on the bare code** — never over a sheet (device pass
 *   2026-10-20, 4) — and leaves through the gate like every other way out.
 * - **The code blurs while the share or waiting sheet is up** (Profile spec, "the
 *   one scrim with a job"; blur over dim, Mark 2026-10-21): a camera can't read
 *   it; clear again the moment either sheet leaves. The lock itself is the
 *   server's — the first scanner claims the room (052), every later phone is
 *   told the code is busy.
 * - **The code blurs under the location prompt too** (step 16, Mark
 *   2026-10-21): the prompt comes first and alone, and the room isn't live
 *   until it's answered (QRScreen), so the code can't be read meanwhile.
 * - **Every open and close starts it bare** (`open`): QROverlay keeps the page
 *   mounted between visits, so the gate is reset each time (device pass, 5a).
 *
 * **Two profile holders (E3b-2a; Mark 2026-10-21: nothing is recorded until
 * both have tapped)** — by a scan, either way, or nearby (useCodePageOffer):
 * - **Their sheet**: their photo above `Connect with <first name>` (a repeat:
 *   `Reconnect with <first name>`); a tap holds the choice and the waiting
 *   sheet reads `Waiting for <first name>` until they tap too; then both land
 *   on People. The waiting X or 60 s: back to the code, nothing recorded (F5).
 * - **A tap outside puts the sheet away** to the `People nearby` strip (F1,
 *   §12b) — the offer stays; the strip brings it back; the caret shows 12px
 *   above it and leaving ends the offer.
 * - **The code tells the truth** (Mark, 2026-10-22): taken — waiting, or a
 *   claimed room mid-exchange — the code blurs (3px) even with no sheet over it.
 * - **The Scan circle** (PXP spec § The foot, L1; D14) opens the in-app
 *   scanner — only on the bare, free code.
 *
 * Not here yet (2b): the listening glow, the one-time teach sheet, the crowd.
 *
 * @param {Object} p
 * @param {string|null} p.profileUserId
 * @param {string} p.qrCodeUrl
 * @param {Object|null} p.profile - the own profile
 * @param {{room: string, nameKnown?: boolean, repeat?: boolean}|null} p.scan -
 *   the scan just made (a new object per scan)
 * @param {(room: string|null) => void} p.onDone - leave to new People
 * @param {boolean} [p.open=true] - the code screen is up (QRScreen's isOpen)
 * @param {() => void} [p.onRearm] - back on the bare code after someone was met
 *   this visit (waiting's X or expiry): the host rotates to a fresh room, so the
 *   next scan is a new exchange, not a join into the last one
 * @param {boolean} [p.awaitingLocation=false] - the location prompt is up and the
 *   room isn't live yet: the code blurs as under the sheets
 * @param {React.ReactNode} [p.readout] - the row under the code (test sites only:
 *   QRScreen's CodeStatusRow); outside the blur, under the sheets
 * @param {Object|null} [p.offer] - two profile holders: pxpOffer's offer (a new
 *   object per offer)
 * @param {Object|null} [p.offerFailed] - the scan behind a nearby match was busy (P4)
 * @param {Object} [p.nearby] - useProximity's: { user, waitingOn, matched, invite, withdraw }
 * @param {Function} [p.onScan] - open the in-app scanner (the Scan circle)
 */
const GuestCodePage = ({
  profileUserId,
  qrCodeUrl,
  profile,
  scan,
  onDone,
  open = true,
  onRearm,
  awaitingLocation = false,
  readout = null,
  offer = null,
  offerFailed = null,
  nearby = null,
  onScan = null,
}) => {
  const [gate, dispatch] = useReducer(codePageGate, initialGate);
  const { loadConnectionData, updateContactSharingStatus } = useConnectionContext();
  const pxp = gate.kind === 'pxp';
  // Each open and each close starts the page bare — declared before the offer
  // and scan effects, so one landing in the same commit as an open still counts
  const leaveRef = useRef(null);
  // The last room formed this visit: the caret still lands on that person
  const lastRoom = useRef(null);
  const gateRef = useRef(gate);
  gateRef.current = gate;
  useEffect(() => {
    // Leaving the code screen mid-offer ends it, nothing recorded (ruling 1)
    const g = gateRef.current;
    if (
      g.kind === 'pxp' &&
      g.stage !== STAGE.REST &&
      g.stage !== STAGE.PEOPLE &&
      g.stage !== STAGE.FORMING
    )
      leaveRef.current?.('left');
    dispatch({ type: 'reset' });
    lastRoom.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const { waitingLabel, leave } = useCodePageOffer({
    gate,
    dispatch,
    offer,
    offerFailed,
    nearby,
    me: profileUserId,
    profile,
    loadConnectionData,
    updateContactSharingStatus,
    onRearm,
  });
  leaveRef.current = leave;
  const [sheetData, setSheetData] = useState(null);

  // The sheet's data, read as the page opens — ready before anyone scans, and
  // never at app start (the page stays mounted closed). `Full profile` ·
  // `Custom` need none of it; your groups / last Custom set appear only if and
  // when it arrives (no special fallback — Mark, 2026-10-20)
  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    loadShareSheetData(profileUserId).then((d) => live && setSheetData(d));
    return () => {
      live = false;
    };
  }, [profileUserId, open]);

  // A scan
  useEffect(() => {
    if (!scan?.room) return;
    lastRoom.current = scan.room;
    dispatch({ type: 'scanned', ...scan });
  }, [scan]);

  // The sheet rises after the scan lands
  useEffect(() => {
    if (gate.stage !== STAGE.FOUND) return undefined;
    const t = setTimeout(() => dispatch({ type: 'rise' }), SHARE_RISE_MS);
    return () => clearTimeout(t);
  }, [gate.stage]);

  // The guest's name — the anon waiting gate's second condition
  const connectionData = useConnectionData(pxp ? null : gate.room);
  const name = connectionData?.anonData?.name || '';
  useEffect(() => {
    if (gate.room && name.trim()) dispatch({ type: 'name', room: gate.room });
  }, [gate.room, name]);

  // Waiting runs out after 60 s (two profile holders: the server's 60 s too)
  useEffect(() => {
    if (gate.stage !== STAGE.WAITING && gate.stage !== STAGE.WAITING_PEER) return undefined;
    const t = setTimeout(() => {
      if (gateRef.current.stage === STAGE.WAITING_PEER) leave('expired');
      dispatch({ type: 'expire' });
    }, WAITING_EXPIRE_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gate.stage]);

  // The room just formed is the one the caret lands on
  useEffect(() => {
    if (pxp && gate.stage === STAGE.PEOPLE && gate.room) lastRoom.current = gate.room;
  }, [pxp, gate.stage, gate.room]);

  // Back on the bare code from the waiting sheet (its X or expiry): a fresh room
  const prevStage = useRef(gate.stage);
  useEffect(() => {
    if (prevStage.current === STAGE.WAITING && gate.stage === STAGE.REST) onRearm?.();
    prevStage.current = gate.stage;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gate.stage]);

  // Through the gate: People, with this person on top
  useEffect(() => {
    if (gate.stage === STAGE.PEOPLE) onDone(gate.room);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gate.stage]);

  const bare = gate.stage === STAGE.REST;
  const away = gate.stage === STAGE.AWAY;
  // Under a sheet, or the code taken (Mark, 2026-10-22): blurred, unreadable
  const dimmed = gate.stage === STAGE.SHARE || codeTaken(gate) || awaitingLocation;
  const waiting = gate.stage === STAGE.WAITING || gate.stage === STAGE.WAITING_PEER;
  const leaveWaiting = () => {
    if (gate.stage === STAGE.WAITING_PEER) leave('dismissed');
    dispatch({ type: 'dismiss' });
  };

  return (
    <div className={s.page}>
      <div className={s.wm}>ROYALE</div>
      <div className={dimmed ? `${s.mid} ${s.dimmed}` : s.mid} data-dimmed={dimmed || undefined}>
        {qrCodeUrl ? (
          <CustomQRDisplay
            profileUserId={profileUserId}
            fallbackUrl={qrCodeUrl}
            photoUrl={profile?.photoUrl}
            photoMeta={profile?.photoMeta}
            size={264}
          />
        ) : null}
      </div>
      {readout ? <div className={s.readout}>{readout}</div> : null}
      {/* A down caret, not an X: the page came up from People and goes back down.
          On the bare code, and 12px above the strip (§12d); never over a sheet.
          From the strip, leaving ends the offer. */}
      {bare || away ? (
        <button
          type="button"
          className={away ? `${s.caret} ${s.caretOverStrip}` : s.caret}
          aria-label="Back to People"
          onClick={() => {
            if (away) leave('left');
            dispatch({ type: 'close', room: lastRoom.current });
          }}
        >
          <CaretDown size={22} />
        </button>
      ) : null}

      {/* The Scan circle (PXP § The foot, L1; D14): the in-app scanner, the one
          way a profile holder in the app scans a code. Only on the bare, free code. */}
      {bare && onScan && !awaitingLocation ? (
        <button type="button" className={s.scan} aria-label="Scan" onClick={onScan}>
          <Scan size={18} />
        </button>
      ) : null}

      <ExchangeShareSheet
        open={gate.stage === STAGE.SHARE}
        room={pxp ? (gate.repeat ? gate.existingRoom : null) : gate.room}
        profile={profile}
        sheetData={sheetData}
        repeat={gate.repeat}
        peer={pxp ? gate.peer : null}
        onChosen={pxp ? (choice) => dispatch({ type: 'chosen', choice }) : null}
        onShared={() => dispatch({ type: 'shared' })}
        onFold={() => dispatch({ type: 'fold' })}
      />

      {/* Put away (F1, §12b): the offer stays; the strip brings the sheet back */}
      <SheetStrip open={away} onClick={() => dispatch({ type: 'restore' })} />

      {/* Waiting (Profile §3.3): dressed down, the bare X its way out; a tap on
          the code under it does nothing. Two profile holders: `Waiting for <first name>`. */}
      <SheetOverlay
        open={waiting}
        quiet
        title={gate.stage === STAGE.WAITING_PEER ? waitingLabel : 'Waiting for connection'}
        onClose={() => {}}
      >
        <SheetDismissX onClick={leaveWaiting} />
        <SheetSpinner />
      </SheetOverlay>
    </div>
  );
};

export default GuestCodePage;
